import React from 'react';
import {Avatar, TscStyles, TscThemeName, TscThemeNames, ControlButton, Glyphs, TscButtonType, TscAvatarSize} from '@techsmith/tsc-cloud-style-guide';
import styled from 'styled-components';
import {AnnotationModes} from '../../constants/Constants';
import FreehandGlyph from '../../static/svg/FreehandGlyph.svg';
import {useTranslation} from 'react-i18next';
import TimeFormatter from '../../util/TimeFormatter';
import AriaHelper from '../../util/AriaHelper';
import {annotationManagerStore, updateAnnotationManagerState} from '../../context/annotationManagerProvider';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {IComment} from '../../model/commentModel';
import FallbackAvatar from '../../static/png/fallback-avatar.png';
import {useTscTheme} from '../../context/themeProvider';
import {mediaStore} from '../../context/mediaProvider';
import dayjs, {UnitType} from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import config from '../../service/config';
import CommentResolve from './CommentResolve';

dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

// Need these values because dayJS showing some comment dates a few seconds in the future when a comment is first submitted
const offsetCount = 5;
const offsetUnit: UnitType = 'second';

const HeaderWrapper = styled.div<{theme: TscThemeNames; isReply: boolean}>`
   display: flex;
   align-items: flext-start;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
`;

const AvatarWrapper = styled.div<{isReply: boolean; isSelected: boolean}>`
   z-index: 1;
   display: block;
   margin: auto;
   border-radius: 100%;
   outline: 2px solid;
   outline-color:${props => props.isSelected && !props.isReply ? TscStyles.color.action.primary : 'transparent'};
   outline-offset: 1px;
   transition: outline 0.25s ease-in;
`;

const MiddleHeaderWrapper = styled.div<{isReply: boolean}>`
   display: flex;
   width: 100%;
   min-width: 0;
   flex-direction: row;
   padding-left: 0.5rem;
   flex-wrap: wrap;
   align-items: center;
   justify-content: left;
   padding-right: 0.5rem;
`;

export const DisplayName = styled.span<{isReply: boolean; failedToLoad: boolean}>`
   margin-left: 0;
   flex: 1 1 auto;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
   width: 100%;
   opacity: 0.9;
   color: ${props => props.failedToLoad ? TscStyles.color.action.destructive : 'currentColor'};
`;

const CommentDateWrapper = styled.div<{theme: TscThemeNames}>`
   font-size: 0.75rem;
   font-weight: 500;
   opacity: 0.75;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
`;

const HeaderRight = styled.div`
   display: flex;
   justify-content: flex-end;
   flex: 1 1 auto;
`;

const DeleteAnnotationButton = styled.div`
   width: 0;

   button {
      border-radius: 50%;
      height: 1rem;
      width: 1rem;
      left: -0.625rem;
      top: -0.375rem;
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
         height: 0.625rem;
         width: 0.625rem
      }
   }
`;

const CommentTimeWrapper = styled.div<{isEditing: boolean; isSelected: boolean}>`
   button {
      width: 3rem;
      padding: 0;
      height: 1.5rem;
      line-height: 1rem;
      letter-spacing: 0;
      margin-left: 0.5rem;
      color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.medium};
      background-color: ${props => props.isSelected ? (props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.medium : TscStyles.color.ui.dawn.lightMedium) : (props.theme === TscThemeName.dusk ? TscStyles.color.action.secondaryDusk : TscStyles.color.ui.dawn.light)};
      span {
         display: flex;
         align-items: center;
         justify-content: center;
      }
   }
`;

const AnnotationButtonWrapper = styled.div<{annotationDisplayed: boolean; deleteAnnotationOnSave: boolean; isSelected: boolean}>`
   button {
      padding: 0.25rem;
      height: 1.5rem;
      width: 1.5rem;
      line-height: 1rem;
      opacity: ${props => props.deleteAnnotationOnSave && '0.5'};
      background-color: ${props => props.isSelected && !props.annotationDisplayed ? (props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.medium : TscStyles.color.ui.dawn.lightMedium) : (props.isSelected ? '' : (props.theme === TscThemeName.dusk ? '' : TscStyles.color.ui.dawn.light))};
      color: ${props => !props.annotationDisplayed ? (props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.medium) : TscStyles.color.text.light};

      :hover {
      }
   }
`;

export const CommentHeaderBase: React.FC<ICommentHeaderProps & IStateMappedProps> = ({annotationMode, isReply, comment, isCommentOwner, deleteAnnotationOnSave, isSelected, setDeleteAnnotationOnSave, onCommentClick, onToggleResolution, IsVideo}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const showEditedLabel: boolean = (!comment.isEditing || !isCommentOwner) && !comment.isDeleted && !!comment.lastModifiedDate;

   const onCommentTimeClick = () => {
      void AriaHelper.readAlert(t('annotation.commentTimeButtonAriaLiveText', {time: TimeFormatter.formatTimeForSpeech(comment.time)}));
      onCommentClick();
   };

   const toggleAnnotations = () => {
      if (annotationMode === AnnotationModes.display) {
         updateAnnotationManagerState({annotationMode: AnnotationModes.none});
      } else {
         updateAnnotationManagerState({annotationMode: AnnotationModes.display});
         deleteAnnotationOnSave && setDeleteAnnotationOnSave(false);
      }
   };

   return (
      <HeaderWrapper
         theme={theme}
         isReply={isReply}
      >
         <AvatarWrapper
            isReply={isReply}
            isSelected={isSelected}
         >
            <Avatar
               avatarImageUrl={comment.user.DisplayName === t('conversations.warnings.failedToLoadUsername') ? FallbackAvatar : comment.user.ProfileImageUrl}
               userDisplayName={comment.user.DisplayName}
               userAltText={null}
               userID={comment.user.Id}
               size={TscAvatarSize.small}
               testId="comment-header-avatar"
            />
         </AvatarWrapper>
         <MiddleHeaderWrapper isReply={isReply}>
            <DisplayName
               isReply={isReply}
               failedToLoad={comment.user.DisplayName === t('conversations.warnings.failedToLoadUsername')}
               data-test-id="comment-header-display-name"
            >
               {comment.user.DisplayName}
            </DisplayName>

            <CommentDateWrapper
               theme={theme}
               title={`${dayjs.utc(comment.createDate).format('LLLL')} UTC`}
            >
               {showEditedLabel && t('conversations.commentEditedText')}
               {dayjs(showEditedLabel ? comment.lastModifiedDate : comment.createDate).subtract(offsetCount, offsetUnit).fromNow()}
            </CommentDateWrapper>

         </MiddleHeaderWrapper>
         <HeaderRight>
            {!!comment.annotation && (
               <>
                  <AnnotationButtonWrapper
                     theme={theme}
                     annotationDisplayed={isSelected && annotationMode === AnnotationModes.display}
                     deleteAnnotationOnSave={deleteAnnotationOnSave}
                     isSelected={isSelected}
                  >
                     <ControlButton
                        onClick={toggleAnnotations}
                        title={annotationMode === AnnotationModes.none ? t('annotation.showAnnotation') : t('annotation.hideAnnotation')}
                        glyph={<FreehandGlyph />}
                        testId="annotation-glyph-button"
                        buttonType={isSelected ? TscButtonType.primary : TscButtonType.secondary}
                        aria={{'aria-pressed': isSelected, 'aria-label': t('annotation.toggleAnnotationLabel')}}
                     />
                  </AnnotationButtonWrapper>
                  {comment.isEditing && !deleteAnnotationOnSave && (
                     <DeleteAnnotationButton>
                        <ControlButton
                           onClick={() => {
                              void AriaHelper.readAlert(t('annotation.annotationPendingDeletionAlert'));
                              updateAnnotationManagerState({annotationMode: AnnotationModes.none});
                              setDeleteAnnotationOnSave(true);
                           }}
                           title={t('annotation.deleteAnnotation')}
                           glyph={<Glyphs.Close16x16 />}
                           testId="delete-annotation-button"
                           buttonType={TscButtonType.secondary}
                           themeName={theme === TscThemeName.dusk ? TscThemeName.dawn : TscThemeName.dusk}
                        />
                     </DeleteAnnotationButton>
                  )}
               </>
            )}
            {IsVideo && !isReply && (
               <CommentTimeWrapper
                  theme={theme}
                  isEditing={comment.isEditing}
                  isSelected={isSelected}
               >
                  <ControlButton
                     onClick={onCommentTimeClick}
                     aria={{'aria-label': TimeFormatter.formatTimeForSpeech(comment.time)}}
                     label={TimeFormatter.formatTime(comment.time)}
                     buttonType={TscButtonType.tertiary}
                     testId="comment-time-button"
                     disabled={comment.isEditing}
                  />
               </CommentTimeWrapper>
            )}
            {(!isReply && !comment.isDeleted) && (
               <CommentResolve
                  userCanResolve={isCommentOwner || config.watchConfig.isOwner || config.watchConfig.isTeamEditor}
                  isResolved={comment.isResolved}
                  isSelected={isSelected}
                  onToggleResolution={onToggleResolution}
               />
            )}
         </HeaderRight>
      </HeaderWrapper>
   );
};

export interface ICommentHeaderProps {
   isReply: boolean;
   comment: IComment;
   deleteAnnotationOnSave: boolean;
   isSelected: boolean;
   setDeleteAnnotationOnSave(deleteAnnotation: boolean): void;
   onCommentClick(): void;
   onToggleResolution(): Promise<void>;
   isCommentOwner: boolean;
}

export interface IStateMappedProps {
   annotationMode: AnnotationModes;
   IsVideo: boolean;
}

const mapStatesToProps = (
   {isReply, comment, deleteAnnotationOnSave, isSelected, isCommentOwner, setDeleteAnnotationOnSave, onCommentClick, onToggleResolution}: ICommentHeaderProps,
   {annotationMode}: Partial<IStateMappedProps>,
   {IsVideo}: Partial<IStateMappedProps>
): ICommentHeaderProps & IStateMappedProps => ({annotationMode, isReply, comment, isCommentOwner, deleteAnnotationOnSave, isSelected, setDeleteAnnotationOnSave, onCommentClick, onToggleResolution, IsVideo});

export default withMemoizedContexts(mapStatesToProps, annotationManagerStore, mediaStore)(CommentHeaderBase);
